import { useParams } from 'react-router-dom'

import { Features } from 'generated/featureDefinitions'
import { RequestOrUpdatePageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import RequestUpdateFlow from 'modules/contracts/components/RequestUpdateFlow'
import UpdateFlow from 'modules/contracts/components/UpdateFlow'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const RequestOrUpdate = () => {
  const { id: employeeId } = useParams()
  const { company, access } = useCurrentSession()
  const { data, loading } = useQuery(RequestOrUpdatePageDocument, {
    variables: {
      companyId: company.id,
      employeeId: String(employeeId),
    },
    skip: !employeeId,
  })

  const hasApprovalSystem =
    data?.features.featuresConnection.nodes.find(
      (feature) => feature.name === 'dev_update_flow_approval_system'
    )?.enabled ?? false
  const isAutoApprover = data?.approvals?.approvalGroupMembershipsConnection?.nodes?.some(
    (membership) => membership?.access?.id === access.id
  )
  const hasToRequestUpdate = hasApprovalSystem ? !isAutoApprover : false
  const referenceContract = data?.contracts.referenceContractsConnection.nodes[0]
  const canEditReferenceContract = referenceContract?._policies?.edit.can ?? false
  const canPromoteReferenceContract = referenceContract?._policies?.promote.can ?? false
  const hasLegacyJobCatalog =
    data?.features?.featuresConnection?.nodes?.find(
      (feature) => feature.name === 'legacy_job_catalog'
    )?.enabled ?? false
  const jobCatalogTreeFeatureEnabled = !hasLegacyJobCatalog
  const hasDevTimeTrackingWorkableDaysDefinition =
    data?.features.featuresConnection.nodes.find(
      (feature) => feature.name === 'dev_time_tracking_workable_days_definition'
    )?.enabled ?? false
  const hasTimeTrackingAnnualisationDailyValue =
    data?.companies.featureAvailabilitiesConnection.nodes.find(
      (feature) => feature.id === Features.time_tracking_annualisation_daily_value
    )?.available ?? false
  const jobCatalogTrees = data?.jobCatalog?.treesConnection?.nodes
  const canEditContractVersion = data?.contracts._policies.contractEditContractVersion.can ?? false
  const jobCatalogContextlessPolicies = data?.jobCatalog?._policies
  const customFieldsContextlessPolicies = data?.customFields._policies
  const hasLegacyContractStructure =
    data?.features?.featuresConnection?.nodes?.find(
      (feature) => feature.name === 'seasonal_contracts_disabled'
    )?.enabled ?? false
  const hasPayrollBundle =
    data?.companies.featureAvailabilitiesConnection.nodes.find(
      (feature) => feature.id === Features.payroll_management
    )?.available ?? false
  const contractVersionPayrollPolicy = data?.contracts.payrollPoliciesConnection.nodes.find(
    (payrollPolicy) => payrollPolicy.policy?.default
  )
  const contractVersionPayrollPolicyId =
    referenceContract?.payrollPolicy?.policy?.id || contractVersionPayrollPolicy?.policy?.id
  const employee = data?.employees.employeesConnection.nodes[0]
  const contractVersions = data?.contracts.contractVersionsConnection.nodes ?? []

  const canAccessFlowsPage =
    canEditReferenceContract || canPromoteReferenceContract || canEditContractVersion

  const hasFixedDiscontinuousContracts =
    data?.companies.featureAvailabilitiesConnection.nodes.some(
      (feature) =>
        feature.id === 'contracts_discontinuous_contracts' &&
        feature.available &&
        !hasLegacyContractStructure
    ) ?? false

  return (
    <ResponsivePage.Body
      variant="fullscreen"
      loading={loading}
      authorized={canAccessFlowsPage}
      error={!employee}
    >
      {hasToRequestUpdate && employee && (
        <RequestUpdateFlow
          contractVersion={referenceContract ?? null}
          contractVersions={contractVersions}
          employee={employee}
          jobCatalogTrees={jobCatalogTrees}
          jobCatalogTreeFeatureEnabled={jobCatalogTreeFeatureEnabled}
          hasDevTimeTrackingWorkableDaysDefinition={hasDevTimeTrackingWorkableDaysDefinition}
          hasTimeTrackingAnnualisationDailyValue={hasTimeTrackingAnnualisationDailyValue}
          contractContextlessPolicies={data?.contracts._policies}
          jobCatalogContextlessPolicies={jobCatalogContextlessPolicies}
          contractVersionPayrollPolicyId={contractVersionPayrollPolicyId}
          hasPayrollBundle={hasPayrollBundle}
          customFieldsContextlessPolicies={customFieldsContextlessPolicies}
        />
      )}
      {!hasToRequestUpdate && employee && (
        <UpdateFlow
          jobCatalogTrees={jobCatalogTrees}
          jobCatalogTreeFeatureEnabled={jobCatalogTreeFeatureEnabled}
          contractVersion={referenceContract ?? null}
          contractVersions={contractVersions}
          employee={employee}
          hasDevTimeTrackingWorkableDaysDefinition={hasDevTimeTrackingWorkableDaysDefinition}
          hasTimeTrackingAnnualisationDailyValue={hasTimeTrackingAnnualisationDailyValue}
          contractContextlessPolicies={data?.contracts._policies}
          jobCatalogContextlessPolicies={jobCatalogContextlessPolicies}
          contractVersionPayrollPolicyId={contractVersionPayrollPolicyId}
          hasPayrollBundle={hasPayrollBundle}
          customFieldsContextlessPolicies={customFieldsContextlessPolicies}
          hasFixedDiscontinuousContracts={hasFixedDiscontinuousContracts}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default RequestOrUpdate
